import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

type LoaderSource = 'axios' | 'global-rx-modal';

export type LoaderProps = {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  startLoading: (source: LoaderSource) => void;
  stopLoading: (source: LoaderSource) => void;
};

export const LoaderContext = createContext<LoaderProps | null>({} as LoaderProps);

export function useLoader() {
  const ctx = useContext(LoaderContext) as LoaderProps;
  return ctx;
}

export default function LoaderProvider({ children }: Readonly<{ children: ReactNode }>): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingSources = useRef(new Map<LoaderSource, number>());

  const startLoading = useCallback((source: LoaderSource) => {
    const count = loadingSources.current.get(source) ?? 0;
    loadingSources.current.set(source, count + 1);
    setIsLoading(true);
  }, []);

  const stopLoading = useCallback((source: LoaderSource) => {
    const count = loadingSources.current.get(source) ?? 0;

    if (count <= 1) {
      loadingSources.current.delete(source);
    } else {
      loadingSources.current.set(source, count - 1);
    }

    if (loadingSources.current.size === 0) {
      setIsLoading(false);
    }
  }, []);

  const loaderValue = useMemo(
    () => ({ isLoading, setIsLoading, startLoading, stopLoading }),
    [isLoading, startLoading, stopLoading],
  );

  return (
    <LoaderContext.Provider value={loaderValue}>
      {children}
    </LoaderContext.Provider>
  );
}
